// Search words for what an icon means, not what it shows: "kindness" finds
// charity and the hearts.  Each term lists a word's common forms, because
// search matches the start of a word ("writing" doesn't find "write"), and
// the icons that word should find.  The terms come from the words used most
// in cog and outcome names.  Icons are 3.5.95 names; search treats these
// words like MDI's aliases.

export interface IconMeaning {
  words: string[];
  icons: string[];
}

export const iconMeanings: IconMeaning[] = [
  {
    words: ['learning', 'learn', 'learner'],
    icons: [
      'school',
      'brain',
      'book-open-page-variant',
      'library',
      'teach',
      'lightbulb-on',
      'notebook',
      'presentation'
    ]
  },
  {
    words: ['knowledge', 'knowing'],
    icons: ['brain', 'book', 'library', 'school', 'lightbulb-on']
  },
  {
    words: ['design', 'designing', 'designer'],
    icons: [
      'math-compass',
      'ruler-square',
      'drawing',
      'palette',
      'vector-bezier',
      'floor-plan',
      'lead-pencil'
    ]
  },
  {
    words: [
      'ideas',
      'idea',
      'ideate',
      'ideation',
      'brainstorm',
      'brainstorming'
    ],
    icons: [
      'lightbulb',
      'lightbulb-on',
      'lightbulb-on-outline',
      'lightbulb-outline',
      'thought-bubble',
      'thought-bubble-outline',
      'brain'
    ]
  },
  {
    words: [
      'creativity',
      'creative',
      'create',
      'creating',
      'creation',
      'imagination',
      'imagine'
    ],
    icons: [
      'palette',
      'brush',
      'lightbulb-on',
      'creation',
      'auto-fix',
      'drawing',
      'thought-bubble'
    ]
  },
  {
    words: ['innovation', 'innovative', 'innovate', 'invention', 'invent'],
    icons: ['rocket', 'lightbulb-on', 'creation', 'flask', 'robot', 'auto-fix']
  },
  {
    words: [
      'thinking',
      'think',
      'thinker',
      'thought',
      'mindset',
      'mind',
      'metacognition',
      'mindsets'
    ],
    icons: [
      'brain',
      'thought-bubble',
      'thought-bubble-outline',
      'lightbulb-on',
      'puzzle'
    ]
  },
  {
    words: [
      'critical',
      'reasoning',
      'reason',
      'logic',
      'logical',
      'critically'
    ],
    icons: [
      'brain',
      'puzzle',
      'chess-knight',
      'scale-balance',
      'magnify',
      'sitemap'
    ]
  },
  {
    words: ['analysis', 'analyze', 'analyse', 'analytical', 'analyzing'],
    icons: [
      'magnify',
      'chart-bar',
      'chart-line',
      'chart-pie',
      'finance',
      'file-chart'
    ]
  },
  {
    words: [
      'evaluation',
      'evaluate',
      'assessment',
      'assess',
      'standards',
      'criteria',
      'criterion',
      'rubric'
    ],
    icons: [
      'clipboard-check',
      'format-list-checks',
      'check-circle',
      'scale-balance',
      'clipboard-text',
      'ruler'
    ]
  },
  {
    words: ['inquiry', 'questions', 'question', 'questioning', 'asking'],
    icons: [
      'help-circle',
      'help-circle-outline',
      'comment-question',
      'comment-question-outline',
      'account-question',
      'help-box'
    ]
  },
  {
    words: ['curiosity', 'curious', 'wonder', 'wondering'],
    icons: ['help-circle', 'magnify', 'telescope', 'binoculars', 'eye', 'cat']
  },
  {
    words: [
      'exploration',
      'explore',
      'exploring',
      'explorer',
      'discovery',
      'discover'
    ],
    icons: [
      'compass',
      'compass-outline',
      'map',
      'binoculars',
      'telescope',
      'hiking',
      'rocket',
      'earth',
      'hiking'
    ]
  },
  {
    words: [
      'research',
      'researching',
      'researcher',
      'investigation',
      'investigate'
    ],
    icons: [
      'magnify',
      'file-search',
      'microscope',
      'flask',
      'telescope',
      'library'
    ]
  },
  {
    words: ['evidence'],
    icons: ['magnify', 'fingerprint', 'file-find', 'clipboard-text']
  },
  {
    words: ['experiment', 'experimentation', 'experimenting', 'testing'],
    icons: [
      'flask',
      'test-tube',
      'beaker',
      'atom',
      'microscope',
      'clipboard-check'
    ]
  },
  {
    words: ['scientific', 'scientist', 'sciences'],
    icons: [
      'atom',
      'flask',
      'microscope',
      'test-tube',
      'dna',
      'beaker',
      'telescope',
      'periodic-table'
    ]
  },
  {
    words: ['mathematical', 'mathematics', 'maths', 'numeracy'],
    icons: [
      'calculator',
      'calculator-variant',
      'math-compass',
      'sigma',
      'pi',
      'plus-minus',
      'division',
      'chart-bell-curve',
      'function-variant'
    ]
  },
  {
    words: [
      'problems',
      'solving',
      'solve',
      'solutions',
      'solution',
      'troubleshooting'
    ],
    icons: [
      'puzzle',
      'puzzle-outline',
      'lightbulb-on',
      'key',
      'wrench',
      'toolbox'
    ]
  },
  {
    words: [
      'making',
      'maker',
      'makers',
      'prototype',
      'prototyping',
      'engineering',
      'engineer',
      'construction',
      'construct'
    ],
    icons: [
      'hammer',
      'wrench',
      'toolbox',
      'robot-industrial',
      'cube-outline',
      'crane',
      'printer-3d',
      'hard-hat',
      'screwdriver'
    ]
  },
  {
    words: [
      'technology',
      'digital',
      'computer',
      'computers',
      'computing',
      'online'
    ],
    icons: [
      'laptop',
      'monitor',
      'cellphone',
      'tablet',
      'chip',
      'robot',
      'web',
      'wifi'
    ]
  },
  {
    words: ['coding', 'programming', 'programmer', 'computational'],
    icons: ['code-tags', 'code-braces', 'console', 'laptop', 'robot', 'chip']
  },
  { words: ['systems', 'system'], icons: ['sitemap', 'cogs', 'settings'] },
  {
    words: ['connections', 'connection', 'connect', 'connecting', 'connected'],
    icons: [
      'link',
      'link-variant',
      'transit-connection-variant',
      'share-variant',
      'vector-polyline',
      'sitemap'
    ]
  },
  {
    words: [
      'writing',
      'write',
      'writer',
      'author',
      'authoring',
      'composition',
      'essay'
    ],
    icons: [
      'pencil',
      'lead-pencil',
      'fountain-pen',
      'pen',
      'feather',
      'script-text',
      'notebook',
      'file-document-edit',
      'pencil-outline',
      'pencil-box',
      'pencil-box-outline',
      'pencil-circle',
      'pencil-circle-outline',
      'fountain-pen-tip'
    ]
  },
  {
    words: ['literacy', 'reading', 'reader', 'readers'],
    icons: [
      'book-open',
      'book-open-page-variant',
      'library',
      'book',
      'glasses',
      'newspaper'
    ]
  },
  {
    words: ['literature', 'poetry', 'poem', 'novel', 'english', 'ela'],
    icons: [
      'book',
      'book-open-page-variant',
      'library-shelves',
      'feather',
      'script-text',
      'library'
    ]
  },
  {
    words: ['grammar', 'vocabulary', 'spelling', 'words'],
    icons: [
      'spellcheck',
      'alphabetical',
      'format-text',
      'dictionary',
      'sort-alphabetical'
    ]
  },
  {
    words: ['story', 'stories', 'storytelling', 'narrative', 'narratives'],
    icons: [
      'book-open-page-variant',
      'book',
      'script-text',
      'drama-masks',
      'movie-roll',
      'library'
    ]
  },
  {
    words: [
      'communication',
      'communicate',
      'communicating',
      'communicator',
      'conversation',
      'discussion',
      'dialogue',
      'discuss'
    ],
    icons: [
      'forum',
      'comment-multiple',
      'comment-text-multiple',
      'chat',
      'message-text',
      'phone-in-talk',
      'bullhorn',
      'voice',
      'email',
      'text-to-speech'
    ]
  },
  {
    words: [
      'speaking',
      'speak',
      'speaker',
      'presentation',
      'presentations',
      'presenting',
      'present',
      'oral',
      'public speaking',
      'explain',
      'explaining',
      'explanation'
    ],
    icons: [
      'microphone',
      'presentation',
      'bullhorn',
      'voice',
      'projector-screen',
      'teach',
      'microphone-outline',
      'text-to-speech'
    ]
  },
  {
    words: ['listening', 'listen', 'listener'],
    icons: ['ear-hearing', 'headphones', 'headset', 'volume-high']
  },
  {
    words: ['audience'],
    icons: ['account-group', 'theater', 'presentation', 'human-male-female']
  },
  {
    words: ['feedback'],
    icons: [
      'comment-multiple',
      'comment-check',
      'forum',
      'thumbs-up-down',
      'message-reply-text'
    ]
  },
  {
    words: [
      'team',
      'teams',
      'teamwork',
      'collaboration',
      'collaborative',
      'collaborate',
      'collaborating',
      'cooperation',
      'cooperate',
      'together',
      'group',
      'groups'
    ],
    icons: [
      'account-group',
      'account-multiple',
      'human-male-female',
      'account-supervisor',
      'home-group',
      'forum',
      'puzzle'
    ]
  },
  {
    words: [
      'community',
      'social',
      'relationships',
      'relationship',
      'belonging',
      'friends',
      'friendship'
    ],
    icons: [
      'account-group',
      'account-multiple',
      'human-male-female',
      'home-group',
      'account-heart',
      'city'
    ]
  },
  {
    words: ['leadership', 'leader', 'leaders', 'leading', 'lead'],
    icons: [
      'crown',
      'flag',
      'account-tie',
      'account-star',
      'star',
      'chess-king',
      'account-supervisor',
      'bullhorn'
    ]
  },
  {
    words: [
      'empathy',
      'empathize',
      'compassion',
      'compassionate',
      'kindness',
      'kind',
      'caring',
      'care'
    ],
    icons: [
      'charity',
      'account-heart',
      'account-heart-outline',
      'heart',
      'heart-outline',
      'cards-heart'
    ]
  },
  {
    words: ['respect', 'respectful'],
    icons: ['account-heart', 'hand-peace', 'scale-balance', 'heart-outline']
  },
  {
    words: [
      'values',
      'value',
      'character',
      'ethics',
      'ethical',
      'integrity',
      'honesty',
      'honest',
      'trust',
      'trustworthy'
    ],
    icons: [
      'scale-balance',
      'shield-check',
      'heart',
      'check-decagram',
      'account-check'
    ]
  },
  {
    words: ['responsibility', 'responsible', 'accountability', 'accountable'],
    icons: ['account-check', 'shield-check', 'clipboard-check', 'key']
  },
  {
    words: ['equity', 'equality', 'fairness', 'fair', 'justice'],
    icons: ['scale-balance', 'gavel', 'account-group', 'hand-peace']
  },
  {
    words: [
      'culture',
      'cultural',
      'cultures',
      'diversity',
      'diverse',
      'inclusion',
      'inclusive',
      'identity'
    ],
    icons: [
      'earth',
      'globe-model',
      'account-group',
      'human-male-female',
      'account-multiple',
      'fingerprint'
    ]
  },
  {
    words: ['global', 'international', 'worldwide'],
    icons: ['earth', 'web', 'globe-model', 'airplane', 'map']
  },
  {
    words: [
      'civic',
      'civics',
      'citizenship',
      'citizen',
      'government',
      'democracy'
    ],
    icons: ['vote', 'vote-outline', 'bank', 'gavel', 'scale-balance', 'flag']
  },
  {
    words: ['history', 'historical', 'past'],
    icons: [
      'history',
      'pillar',
      'castle',
      'script',
      'timer-sand',
      'globe-model'
    ]
  },
  {
    words: ['arts', 'artistic', 'artist', 'artwork', 'visual'],
    icons: [
      'palette',
      'brush',
      'drawing',
      'format-paint',
      'spray',
      'drama-masks'
    ]
  },
  {
    words: ['emotional', 'emotions', 'emotion', 'feelings', 'feeling'],
    icons: [
      'emoticon',
      'emoticon-happy',
      'emoticon-sad',
      'emoticon-neutral',
      'emoticon-angry',
      'emoticon-cry',
      'emoticon-excited',
      'heart',
      'drama-masks'
    ]
  },
  {
    words: [
      'wellness',
      'wellbeing',
      'well-being',
      'healthy',
      'health',
      'self-care'
    ],
    icons: ['heart-pulse', 'food-apple', 'run', 'spa', 'dumbbell', 'sleep']
  },
  {
    words: ['physical', 'fitness', 'exercise', 'movement', 'active'],
    icons: [
      'run',
      'dumbbell',
      'basketball',
      'soccer',
      'bike',
      'swim',
      'karate',
      'walk'
    ]
  },
  {
    words: [
      'calm',
      'mindfulness',
      'mindful',
      'relax',
      'relaxation',
      'peaceful',
      'peace'
    ],
    icons: [
      'spa',
      'spa-outline',
      'yin-yang',
      'peace',
      'leaf',
      'weather-sunset',
      'beach'
    ]
  },
  {
    words: ['quiet', 'silent', 'silence'],
    icons: ['volume-off', 'volume-mute', 'microphone-off', 'sleep']
  },
  {
    words: ['safety', 'safe', 'secure'],
    icons: [
      'shield-check',
      'hard-hat',
      'security',
      'lock',
      'lifebuoy',
      'safety-goggles',
      'medical-bag',
      'traffic-light',
      'shield-check-outline'
    ]
  },
  {
    words: [
      'environment',
      'environmental',
      'sustainability',
      'sustainable',
      'climate',
      'ecology',
      'green'
    ],
    icons: [
      'earth',
      'leaf',
      'tree',
      'recycle',
      'pine-tree',
      'solar-power',
      'water',
      'wind-turbine'
    ]
  },
  {
    words: [
      'growth',
      'grow',
      'growing',
      'development',
      'develop',
      'developing',
      'progress',
      'improvement',
      'improve'
    ],
    icons: [
      'trending-up',
      'chart-line',
      'stairs',
      'tree',
      'flower',
      'leaf',
      'chart-areaspline',
      'finance'
    ]
  },
  {
    words: ['goals', 'goal', 'aim', 'objective', 'objectives', 'target'],
    icons: [
      'target',
      'bullseye',
      'bullseye-arrow',
      'flag-checkered',
      'summit',
      'trophy',
      'flag'
    ]
  },
  {
    words: ['purpose', 'meaning', 'meaningful', 'direction'],
    icons: ['compass', 'compass-outline', 'target', 'bullseye', 'map-marker']
  },
  { words: ['impact'], icons: ['bullseye-arrow', 'trending-up', 'earth'] },
  {
    words: ['action', 'act'],
    icons: ['run', 'rocket', 'play-circle', 'flash']
  },
  {
    words: ['challenge', 'challenges', 'challenging'],
    icons: ['summit', 'terrain', 'stairs', 'dumbbell', 'flag-checkered']
  },
  {
    words: [
      'perseverance',
      'persevere',
      'persistence',
      'persistent',
      'resilience',
      'resilient',
      'grit',
      'determination',
      'determined'
    ],
    icons: [
      'summit',
      'terrain',
      'stairs',
      'flag-checkered',
      'dumbbell',
      'shield',
      'tortoise'
    ]
  },
  {
    words: [
      'confidence',
      'confident',
      'courage',
      'courageous',
      'brave',
      'bravery'
    ],
    icons: ['shield', 'account-star', 'star-face', 'crown', 'human-handsup']
  },
  {
    words: ['patience', 'patient', 'waiting', 'wait'],
    icons: [
      'timer-sand',
      'timer-sand-empty',
      'tortoise',
      'clock-outline',
      'spa',
      'timer-sand-full'
    ]
  },
  {
    words: [
      'mastery',
      'master',
      'proficiency',
      'proficient',
      'expert',
      'excellence',
      'achievement',
      'achieve',
      'success',
      'successful',
      'accomplishment'
    ],
    icons: [
      'trophy',
      'medal',
      'star',
      'crown',
      'certificate',
      'seal',
      'podium',
      'check-decagram',
      'ribbon',
      'trophy-outline',
      'trophy-variant',
      'trophy-variant-outline',
      'trophy-award',
      'podium-gold',
      'summit'
    ]
  },
  {
    words: [
      'motivation',
      'motivated',
      'initiative',
      'drive',
      'ambition',
      'passion'
    ],
    icons: ['fire', 'rocket', 'flash', 'trending-up', 'star-face']
  },
  {
    words: [
      'practice',
      'practicing',
      'practise',
      'rehearse',
      'rehearsal',
      'habits',
      'habit',
      'routine'
    ],
    icons: [
      'repeat',
      'dumbbell',
      'target',
      'metronome',
      'calendar-check',
      'check-all'
    ]
  },
  {
    words: [
      'organization',
      'organisation',
      'organize',
      'organise',
      'organized',
      'organizing',
      'planning',
      'management',
      'manage',
      'structure',
      'structured'
    ],
    icons: [
      'folder',
      'format-list-checks',
      'sitemap',
      'file-cabinet',
      'calendar-check',
      'clipboard-check',
      'timetable',
      'view-dashboard'
    ]
  },
  {
    words: [
      'independence',
      'independent',
      'autonomy',
      'agency',
      'self-directed'
    ],
    icons: ['compass', 'steering', 'key', 'account-star']
  },
  {
    words: [
      'reflection',
      'reflect',
      'reflecting',
      'reflective',
      'self-awareness',
      'awareness'
    ],
    icons: [
      'thought-bubble',
      'thought-bubble-outline',
      'spa',
      'account-circle',
      'eye'
    ]
  },
  {
    words: ['perspectives', 'perspective', 'viewpoint', 'point of view'],
    icons: ['eye', 'glasses', 'binoculars', 'account-multiple']
  },
  {
    words: ['focus', 'focused', 'attention', 'concentration', 'concentrate'],
    icons: [
      'target',
      'bullseye',
      'crosshairs',
      'eye',
      'image-filter-center-focus',
      'magnify'
    ]
  },
  {
    words: [
      'adaptability',
      'adaptable',
      'adapt',
      'flexibility',
      'flexible',
      'change'
    ],
    icons: ['autorenew', 'sync', 'shuffle-variant', 'swap-horizontal']
  },
  {
    words: [
      'support',
      'supporting',
      'supportive',
      'helping',
      'helpful',
      'help',
      'assist'
    ],
    icons: ['lifebuoy', 'charity', 'account-heart', 'face-agent']
  },
  {
    words: [
      'career',
      'careers',
      'job',
      'jobs',
      'workplace',
      'professional',
      'professionalism'
    ],
    icons: ['briefcase', 'account-tie', 'school', 'domain', 'briefcase-outline']
  },
  {
    words: ['entrepreneurship', 'entrepreneur', 'business', 'startup'],
    icons: [
      'briefcase',
      'store',
      'cash',
      'chart-line',
      'rocket',
      'lightbulb-on'
    ]
  },
  {
    words: [
      'financial',
      'finance',
      'money',
      'economics',
      'budget',
      'budgeting'
    ],
    icons: ['cash', 'bank', 'currency-usd', 'coins', 'wallet', 'finance']
  },
  { words: ['future'], icons: ['rocket', 'crystal-ball', 'telescope'] },
  {
    words: ['gratitude', 'grateful', 'thankful', 'thanks'],
    icons: ['heart-outline', 'charity', 'emoticon-happy']
  },
  {
    words: ['cold', 'winter', 'freezing', 'chilly'],
    icons: [
      'snowflake',
      'snowman',
      'thermometer-minus',
      'ice-cream',
      'weather-snowy'
    ]
  },
  {
    words: ['hot', 'heat', 'warm', 'summer'],
    icons: [
      'fire',
      'weather-sunny',
      'white-balance-sunny',
      'thermometer-plus',
      'chili-hot',
      'beach',
      'coffee'
    ]
  },
  {
    words: ['fast', 'speed', 'quick', 'quickly'],
    icons: [
      'run-fast',
      'speedometer',
      'rocket',
      'flash',
      'clock-fast',
      'car-sports',
      'rabbit'
    ]
  },
  { words: ['slow', 'slowly'], icons: ['tortoise', 'turtle', 'timer-sand'] },
  {
    words: ['love', 'loving', 'like', 'likes'],
    icons: [
      'heart',
      'heart-outline',
      'heart-box',
      'heart-box-outline',
      'heart-circle',
      'heart-circle-outline',
      'heart-multiple',
      'heart-multiple-outline',
      'cards-heart',
      'home-heart',
      'emoticon-kiss',
      'emoticon-kiss-outline',
      'tag-heart',
      'tag-heart-outline'
    ]
  },
  {
    words: [
      'winner',
      'winning',
      'win',
      'champion',
      'champions',
      'first place',
      'ranking'
    ],
    icons: [
      'trophy',
      'trophy-outline',
      'trophy-variant',
      'trophy-variant-outline',
      'trophy-award',
      'podium',
      'podium-gold',
      'medal'
    ]
  },
  {
    words: [
      'seeing',
      'see',
      'look',
      'looking',
      'watch',
      'watching',
      'observe',
      'observation',
      'vision'
    ],
    icons: [
      'eye',
      'eye-outline',
      'eye-circle',
      'eye-circle-outline',
      'binoculars'
    ]
  },
  {
    words: ['law', 'laws', 'legal', 'judge', 'rules'],
    icons: ['gavel', 'scale-balance']
  },
  {
    words: ['magic', 'magical'],
    icons: ['auto-fix', 'creation', 'crystal-ball']
  },
  { words: ['luck', 'lucky'], icons: ['clover', 'horseshoe'] },
  {
    words: [
      'celebrate',
      'celebration',
      'cheer',
      'hooray',
      'excited',
      'excitement'
    ],
    icons: ['human-handsup', 'emoticon-excited']
  },
  {
    words: ['yes', 'agree', 'approve', 'good', 'thumbs up'],
    icons: ['thumb-up', 'thumb-up-outline']
  },
  {
    words: ['no', 'disagree', 'bad', 'thumbs down'],
    icons: ['thumb-down', 'thumb-down-outline']
  },
  {
    words: ['opinion', 'opinions', 'rating', 'vote', 'voting'],
    icons: ['thumbs-up-down', 'vote', 'vote-outline', 'poll']
  },
  {
    words: ['space', 'astronomy', 'stars', 'planets'],
    icons: ['telescope', 'rocket', 'earth', 'weather-night', 'orbit']
  },
  {
    words: ['energy', 'energetic', 'power'],
    icons: [
      'flash',
      'flash-outline',
      'flash-circle',
      'battery-charging',
      'solar-power'
    ]
  },
  {
    words: ['balance', 'balanced', 'harmony'],
    icons: ['yin-yang', 'scale-balance']
  },
  {
    words: ['dream', 'dreams', 'dreaming'],
    icons: [
      'thought-bubble',
      'thought-bubble-outline',
      'weather-night',
      'sleep'
    ]
  },
  {
    words: ['protect', 'protection', 'protecting'],
    icons: ['shield-check', 'shield-check-outline', 'shield', 'security']
  },
  {
    words: ['sing', 'singing', 'singer', 'voice'],
    icons: ['microphone', 'microphone-outline', 'microphone-variant', 'music']
  },
  {
    words: ['hello', 'hi', 'greeting', 'welcome', 'wave'],
    icons: ['hail', 'hand-peace']
  },
  {
    words: ['coach', 'coaching', 'referee'],
    icons: ['whistle', 'account-tie']
  },
  {
    words: ['vacation', 'holiday', 'travel', 'trip'],
    icons: ['beach', 'airplane', 'wallet-travel', 'map']
  },
  {
    words: ['king', 'queen', 'royal'],
    icons: ['crown', 'chess-king', 'chess-queen']
  },
  {
    words: ['increase', 'rise', 'decrease', 'decline', 'trend', 'trends'],
    icons: ['trending-up', 'trending-down', 'chart-line']
  },
  { words: ['geography'], icons: ['globe-model', 'earth', 'map'] },
  { words: ['rescue'], icons: ['lifebuoy', 'medical-bag'] },
  {
    words: ['pet', 'pets', 'dog'],
    icons: ['dog', 'dog-side', 'paw', 'bone', 'cat']
  },
  {
    words: [
      'teaching',
      'teacher',
      'teachers',
      'lesson',
      'lessons',
      'instruction',
      'classroom'
    ],
    icons: [
      'teach',
      'presentation',
      'school',
      'chair-school',
      'projector-screen'
    ]
  },
  {
    words: ['giving', 'generosity', 'generous', 'sharing', 'share'],
    icons: ['charity', 'heart-outline', 'share-variant']
  },
  {
    words: ['sleep', 'sleeping', 'rest', 'resting', 'tired'],
    icons: ['bed-empty', 'sleep', 'weather-night', 'hotel', 'power-sleep']
  },
  {
    words: [
      'accessibility',
      'accessible',
      'disability',
      'disabilities',
      'blind',
      'deaf'
    ],
    icons: ['wheelchair-accessibility', 'braille', 'ear-hearing', 'glasses']
  },
  {
    words: [
      'engagement',
      'engage',
      'engaged',
      'engaging',
      'participation',
      'participate'
    ],
    icons: [
      'hail',
      'human-handsup',
      'forum',
      'comment-multiple',
      'account-group'
    ]
  },
  {
    words: ['strategy', 'strategies', 'strategic', 'tactics'],
    icons: ['chess-knight', 'chess-king', 'sitemap', 'target', 'map']
  }
];
